// src/pages/settings/SitesTab.tsx — onglet Sites (liste, filtre client, recherche, création/édition/suppression)

import { useState } from "react";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { BtnEdit, BtnDelete, WarningBadge } from "../../components/IconButtons";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import type { UserRole } from "../../auth/roles";
import type { LatLon } from "../../api/geoApi";
import Modal from "./Modal";
import { SiteForm } from "./SiteForm";
import { SitesMap } from "./SitesMap";
import { WeatherPointModal } from "./WeatherPointModal";
import { ClientFilterChips } from "./ClientFilterChips";
import { siteLatLon, siteHasWeatherStation } from "./helpers";

type SitesView = "list" | "map";

export function SitesTab({ clients, sites, onSave, onDelete, onRefresh, setModal, showToast, userRole }:{
  clients: ClientRecord[]; sites: SiteRecord[];
  onSave: (s: SiteRecord) => Promise<void>;
  onDelete: (s: SiteRecord) => Promise<void>;
  onRefresh: () => Promise<void>;
  setModal: (m: ConfirmModalProps | null) => void;
  showToast: (msg: string) => void;
  userRole?: UserRole;
}) {
  const [showForm,     setShowForm]     = useState(false);
  const [editSite,     setEditSite]     = useState<SiteRecord|undefined>();
  const [filterClient, setFilterClient] = useState("");
  const [query,        setQuery]        = useState("");
  const [view,         setView]         = useState<SitesView>("list");
  const [weatherPoint, setWeatherPoint] = useState<{ site: SiteRecord; coord: LatLon } | null>(null);
  const [refreshing,   setRefreshing]   = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };

  const qs = query.trim().toLowerCase();
  const filtered = sites
    .filter(s => !filterClient || s.clientId === filterClient)
    .filter(s => !qs || s.name.toLowerCase().includes(qs) || s.id.toLowerCase().includes(qs));

  const existingSiteIds = sites.map(s=>s.id);

  const handleSave = async (s: SiteRecord) => {
    await onSave(s);
    showToast("Site enregistré ✓");
  };

  // Garde la fiche site de la modale météo à jour après un enregistrement (choix de station),
  // pour qu'elle recalcule correctement son état "modifications non enregistrées".
  const handleSaveWeather = async (updated: SiteRecord) => {
    await handleSave(updated);
    setWeatherPoint(wp => wp ? { ...wp, site: updated } : wp);
  };

  const handleDelete = (s: SiteRecord) => {
    setModal({ title:"Supprimer ce site ?", message:`Le site "${s.name}" sera définitivement supprimé.`,
      confirmLabel:"Supprimer", danger:true, onCancel:()=>setModal(null),
      onConfirm: async () => { await onDelete(s); setModal(null); showToast("Site supprimé"); },
    });
  };

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      <div style={{flexShrink:0,paddingBottom:12}}>
        <div className="flex-between mb-12">
          <p style={{fontSize:13,color:"var(--text-muted)"}}>
            {(filterClient || qs)
              ? `${filtered.length} / ${sites.length} site${sites.length!==1?"s":""}`
              : `${sites.length} site${sites.length!==1?"s":""} configuré${sites.length!==1?"s":""}.`}
            {clients.length===0 && " ⚠ Créez d'abord un client."}
          </p>
          <div className="flex gap-8" style={{alignItems:"center"}}>
            <div style={{display:"flex",background:"#f2f2f2",borderRadius:8,padding:3}}>
              {(["list","map"] as SitesView[]).map(v=>(
                <button key={v} onClick={()=>setView(v)}
                  title={v==="list"?"Vue liste":"Vue carte"}
                  style={{border:"none",cursor:"pointer",padding:"5px 10px",borderRadius:6,
                    fontFamily:"var(--ff-title)",fontSize:12,fontWeight:700,
                    background:view===v?"#fff":"transparent",
                    color:view===v?"var(--text-primary)":"var(--text-muted)",
                    boxShadow:view===v?"0 1px 4px rgba(0,0,0,0.10)":"none",
                    transition:"all 0.15s"}}>
                  {v==="list" ? "☰ Liste" : "🗺 Carte"}
                </button>
              ))}
            </div>
            <button onClick={handleRefresh} disabled={refreshing} className="btn btn-ghost btn-sm">↻ Rafraîchir</button>
            <button onClick={()=>{setEditSite(undefined);setShowForm(true);}} className="btn btn-black btn-sm" disabled={clients.length===0} style={{border:"1.5px solid transparent"}}>
              ＋ Ajouter un site
            </button>
          </div>
        </div>
        <input
          className="field mb-12"
          placeholder="Rechercher un site…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ width:"100%", boxSizing:"border-box" }}
        />
        <ClientFilterChips clients={clients} active={filterClient} onChange={setFilterClient}
          count={id => id === "" ? sites.length : sites.filter(s=>s.clientId===id).length}/>
      </div>
      {view==="map" ? (
        <div style={{flex:1,minHeight:0,paddingBottom:24}}>
          <SitesMap sites={filtered} clients={clients}
            onEdit={s=>{setEditSite(s);setShowForm(true);}} onDelete={handleDelete}
            onSaveSite={handleSave} onSave={onSave} userRole={userRole} setModal={setModal} showToast={showToast}/>
        </div>
      ) : (
      <div style={{flex:1,minHeight:0,overflowY:"auto",paddingBottom:24}}>
        {sites.length===0
          ? <div className="card p-32" style={{textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:10}}>🏗</div>
              <p style={{fontSize:13,color:"var(--text-muted)"}}>Aucun site configuré.</p>
            </div>
          : filtered.length === 0
            ? <div className="card p-32" style={{textAlign:"center",color:"var(--text-faint)"}}>
                <div style={{fontSize:28,marginBottom:10}}>🔎</div>
                {qs
                  ? <p style={{fontSize:13,marginBottom:12}}>Aucun résultat pour « {query} ».</p>
                  : <p style={{fontSize:13,marginBottom:12}}>Aucun site pour ce client.</p>}
                <button onClick={()=>{setFilterClient(""); setQuery("");}} className="btn btn-ghost btn-sm">
                  Voir tous les sites
                </button>
              </div>
            : <div className="flex-col gap-10">
                {filtered.map(s=>{
                  const client=clients.find(c=>c.id===s.clientId);
                  const photo=s.data.coverProps?.photoUrl;
                  const coord=siteLatLon(s);
                  return(
                    <div key={s.id} className="card flex gap-16" style={{alignItems:"center",padding:"14px 18px"}}>
                      {photo
                        ? <img src={photo} alt="" style={{width:56,height:40,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                        : <div style={{width:56,height:40,borderRadius:6,background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🏗</div>}
                      <div className="flex-1">
                        <div className="t-title" style={{fontSize:14,color:"var(--text-primary)"}}>{s.name}</div>
                        <div style={{fontSize:11,color:"var(--text-muted)"}}>
                          {client?.name??s.clientId}
                          {s.pdls.length>0 && <> · ⚡ {s.pdls.length} PDL</>}
                          {s.pces.length>0 && <> · 🔥 {s.pces.length} PCE</>}
                          {s.pdls.length===0 && s.pces.length===0 && <> · aucun compteur</>}
                        </div>
                        {s.data.description && (
                          <div style={{fontSize:11,color:"var(--text-faint)",marginTop:2}}>{s.data.description}</div>
                        )}
                      </div>
                      <div className="flex gap-8">
                        <div style={{position:"relative",flexShrink:0}}>
                          <button onClick={()=>coord && setWeatherPoint({site:s, coord})} disabled={!coord}
                            title={!coord
                              ? "Site non géolocalisé — enregistrez une adresse valide pour ce site (onglet Info)"
                              : siteHasWeatherStation(s) ? "Températures & DJU — stations proches" : "Aucune station Météo-France rattachée — les blocs DJU du rapport (rigueur climatique, prévision) échoueront"}
                            style={{background:"none",border:"1.5px solid #ddd",borderRadius:8,
                              width:36,height:36,cursor:coord?"pointer":"not-allowed",opacity:coord?1:0.5,fontSize:16,
                              display:"flex",alignItems:"center",justifyContent:"center"}}>
                            🌡
                          </button>
                          {!coord && <WarningBadge title="Site non géolocalisé"/>}
                          {coord && !siteHasWeatherStation(s) && <WarningBadge title="Aucune station Météo-France rattachée"/>}
                        </div>
                        <BtnEdit onClick={()=>{setEditSite(s);setShowForm(true);}} title="Modifier le site"/>
                        <BtnDelete onClick={()=>handleDelete(s)} title="Supprimer le site"/>
                      </div>
                    </div>
                  );
                })}
              </div>
        }
      </div>
      )}

      {showForm && (
        <Modal title={editSite?`Modifier le site — ${editSite.name}`:"Nouveau site"} onClose={()=>setShowForm(false)} wide fixedHeight>
          <SiteForm initial={editSite} clients={clients} sites={sites}
            existingIds={existingSiteIds.filter(id=>id!==editSite?.id)}
            onSave={handleSave} onClose={()=>setShowForm(false)}
            userRole={userRole} setModal={setModal}/>
        </Modal>
      )}

      {weatherPoint && (
        <WeatherPointModal site={weatherPoint.site} lat={weatherPoint.coord.lat} lon={weatherPoint.coord.lon}
          onClose={()=>setWeatherPoint(null)} onSaveSite={handleSaveWeather}/>
      )}
    </div>
  );
}
